import { useEffect, useState } from 'react';
import { Building2, Plus, Loader2, Pencil, Trash2, Eye, ImagePlus, X, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import CnibMap from '../../components/CnibMap';
import { DEPARTMENTS, PROPERTY_TYPES, OPERATIONS, PROPERTY_TYPE_LABELS, OPERATION_LABELS, PROPERTY_STATUS_LABELS } from '../../lib/constants';

const EMPTY = {
  title: '', property_type: 'departamento', operation: 'venta', price_usd: '', city: '', zone: '',
  address: '', lat: null, lng: null, bedrooms: 0, bathrooms: 0, area_m2: 0, description: '', images: [], status: 'available',
};

const STATUS_BADGE = {
  available: 'bg-emerald-500/10 text-emerald-600',
  reserved: 'bg-amber-500/10 text-amber-600',
  sold: 'bg-slate-500/10 text-slate-500',
};

export default function MemberProperties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    api
      .get('/properties/mine')
      .then(({ data }) => setProperties(data))
      .catch(() => toast.error('No se pudieron cargar tus propiedades'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p.property_id);
    setForm({
      title: p.title, property_type: p.property_type, operation: p.operation, price_usd: p.price_usd,
      city: p.city, zone: p.zone || '', address: p.address || '', lat: p.lat, lng: p.lng,
      bedrooms: p.bedrooms || 0, bathrooms: p.bathrooms || 0, area_m2: p.area_m2 || 0,
      description: p.description || '', images: p.images || [], status: p.status || 'available',
    });
    setShowForm(true);
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        urls.push(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...urls] }));
      toast.success(`${urls.length} foto(s) subida(s).`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir la imagen.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price_usd: Number(form.price_usd) || 0,
      bedrooms: Number(form.bedrooms) || 0,
      bathrooms: Number(form.bathrooms) || 0,
      area_m2: Number(form.area_m2) || 0,
    };
    try {
      if (editing) {
        await api.patch(`/properties/${editing}`, payload);
        toast.success('Propiedad actualizada.');
      } else {
        await api.post('/properties', payload);
        toast.success('Propiedad publicada en la red.');
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar la propiedad.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta propiedad de la red?')) return;
    try {
      await api.delete(`/properties/${id}`);
      toast.success('Propiedad eliminada.');
      setProperties(properties.filter((p) => p.property_id !== id));
    } catch {
      toast.error('No se pudo eliminar.');
    }
  };

  const inputCls =
    'w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15';

  return (
    <div className="space-y-6" data-testid="member-properties-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">MLS</p>
          <h1 className="mt-1 font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">Mis propiedades</h1>
        </div>
        <button
          onClick={openNew}
          data-testid="new-property-button"
          className="flex items-center gap-2 rounded-full bg-orange-500 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-orange-600"
        >
          <Plus className="h-4 w-4" /> Nueva propiedad
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6" data-testid="property-form">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-slate-900">{editing ? 'Editar propiedad' : 'Publicar propiedad'}</h2>
            <button type="button" onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-semibold text-slate-700">Título *</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required minLength={4} className={inputCls} placeholder="Ej. Departamento moderno en Equipetrol" data-testid="property-title-input" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Tipo *</label>
              <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className={inputCls} data-testid="property-type-select">
                {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Operación *</label>
              <select value={form.operation} onChange={(e) => setForm({ ...form, operation: e.target.value })} className={inputCls} data-testid="property-operation-select">
                {OPERATIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Precio (US$) *</label>
              <input type="number" min="0" step="any" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: e.target.value })} required className={inputCls} data-testid="property-price-input" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Estado</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls} data-testid="property-status-select">
                {Object.entries(PROPERTY_STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Departamento *</label>
              <select value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required className={inputCls} data-testid="property-city-select">
                <option value="">Selecciona…</option>
                {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Zona / Barrio</label>
              <input value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} className={inputCls} placeholder="Ej. Equipetrol Norte" data-testid="property-zone-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-semibold text-slate-700">Dirección referencial</label>
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} data-testid="property-address-input" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Dormitorios</label>
              <input type="number" min="0" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} className={inputCls} data-testid="property-bedrooms-input" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Baños</label>
              <input type="number" min="0" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} className={inputCls} data-testid="property-bathrooms-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-semibold text-slate-700">Superficie (m²)</label>
              <input type="number" min="0" step="any" value={form.area_m2} onChange={(e) => setForm({ ...form, area_m2: e.target.value })} className={inputCls} data-testid="property-area-input" />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">
              Ubicación en el mapa <span className="font-normal text-slate-400">(haz clic para marcar el punto)</span>
            </label>
            <CnibMap
              center={form.lng ? [form.lng, form.lat] : [-63.18, -17.78]}
              zoom={form.lng ? 13 : 11}
              onMapClick={({ lat, lng }) => setForm((f) => ({ ...f, lat, lng }))}
              markers={form.lat ? [{ id: 'new', lat: form.lat, lng: form.lng, color: '#F97316' }] : []}
              className="h-56 w-full overflow-hidden rounded-xl border border-slate-200"
            />
            {form.lat && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-slate-500">
                <MapPin className="h-3 w-3 text-orange-500" /> {form.lat.toFixed(5)}, {form.lng.toFixed(5)}
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">Fotos</label>
            <div className="flex flex-wrap gap-3">
              {form.images.map((img, i) => (
                <div key={i} className="group relative h-20 w-28 overflow-hidden rounded-xl">
                  <img src={img} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }))}
                    className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <label
                className={`flex h-20 w-28 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-300 text-slate-400 transition-colors hover:border-cnib-blue hover:text-cnib-blue ${uploading ? 'pointer-events-none opacity-50' : ''}`}
                data-testid="property-photo-upload"
              >
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                <span className="text-[10px] font-semibold">{uploading ? 'Subiendo…' : 'Subir'}</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
              </label>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">Descripción</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className={inputCls} placeholder="Características, amenities, formas de pago…" data-testid="property-description-input" />
          </div>

          <button type="submit" disabled={saving} data-testid="property-save-button" className="flex items-center gap-2 rounded-full bg-cnib-blue px-7 py-3 text-sm font-bold text-white transition-colors hover:bg-cnib-bluehover disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {editing ? 'Guardar cambios' : 'Publicar propiedad'}
          </button>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-cnib-blue" /></div>
      ) : properties.length === 0 && !showForm ? (
        <div className="rounded-2xl border border-dashed border-slate-300 py-16 text-center" data-testid="properties-empty-state">
          <Building2 className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Aún no publicaste propiedades.</p>
          <button onClick={openNew} className="mt-4 rounded-full bg-orange-500 px-6 py-2.5 text-sm font-bold text-white hover:bg-orange-600">
            Publicar la primera
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="member-properties-grid">
          {properties.map((p) => (
            <div key={p.property_id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`my-property-${p.property_id}`}>
              <div className="relative h-36 bg-slate-100">
                {p.images?.[0] ? (
                  <img src={p.images[0]} alt={p.title} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center"><Building2 className="h-8 w-8 text-slate-300" /></div>
                )}
                <span className={`absolute left-3 top-3 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${STATUS_BADGE[p.status]}`}>
                  {PROPERTY_STATUS_LABELS[p.status]}
                </span>
                {!p.listed && (
                  <span className="absolute right-3 top-3 rounded-full bg-red-500/90 px-2.5 py-0.5 text-[10px] font-bold uppercase text-white">Oculta</span>
                )}
              </div>
              <div className="p-4">
                <p className="truncate font-heading text-sm font-bold text-slate-900">{p.title}</p>
                <p className="mt-0.5 text-xs text-slate-500">{OPERATION_LABELS[p.operation]} · {PROPERTY_TYPE_LABELS[p.property_type]} · {p.city}</p>
                <div className="mt-2 flex items-center justify-between">
                  <p className="font-heading text-base font-extrabold text-cnib-blue">US$ {Number(p.price_usd).toLocaleString('es-BO')}</p>
                  <span className="flex items-center gap-1 text-xs text-slate-400"><Eye className="h-3.5 w-3.5" /> {p.views}</span>
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => openEdit(p)} data-testid={`edit-property-${p.property_id}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-cnib-blue/10 py-2 text-xs font-bold text-cnib-blue transition-colors hover:bg-cnib-blue/20">
                    <Pencil className="h-3.5 w-3.5" /> Editar
                  </button>
                  <button onClick={() => handleDelete(p.property_id)} data-testid={`delete-property-${p.property_id}`} className="flex items-center justify-center rounded-lg bg-red-500/10 px-3 py-2 text-red-600 transition-colors hover:bg-red-500/20">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
